import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../context/shared/AuthContext';
import apiService from '../../services/shared/apiService';
import { TimelineEntry } from '../../shared/types/src';
import { BookmarkIcon } from '@heroicons/react/24/outline';

interface SymbolicTimelineProps {
  userId: string;
}

const SymbolicTimeline: React.FC<SymbolicTimelineProps> = ({ userId }) => {
  const { user } = useAuth();
  const [savingAnchors, setSavingAnchors] = useState<Record<string, boolean>>({});
  
  const { data: timelineData, isLoading } = useQuery({
    queryKey: ['symbolic-timeline', userId],
    queryFn: () => apiService.getPinnedTimeline(userId),
    enabled: !!userId,
  });

  // Color mapping for different tag categories
  const getTagColor = (tag: string) => {
    const positiveTags = ['joy', 'growth', 'confidence', 'energy', 'clarity', 'gratitude', 'peace', 'strength', 'resilience', 'healing'];
    const negativeTags = ['anxiety', 'sadness', 'anger', 'fatigue', 'confusion', 'overwhelm', 'isolation', 'shame', 'hopelessness', 'inertia'];
    const neutralTags = ['reflection', 'uncertainty', 'questioning', 'rest', 'sleep', 'focus', 'scatter', 'connection', 'lonely'];

    if (positiveTags.includes(tag)) return 'bg-green-100 text-green-800';
    if (negativeTags.includes(tag)) return 'bg-red-100 text-red-800';
    if (neutralTags.includes(tag)) return 'bg-blue-100 text-blue-800';
    return 'bg-gray-100 text-gray-800';
  };

  // Emoji mapping for tags
  const getTagEmoji = (tag: string) => {
    const emojiMap: Record<string, string> = {
      joy: '😊', growth: '🌱', confidence: '💪', energy: '⚡', clarity: '✨',
      gratitude: '🙏', peace: '🕊️', strength: '💪', resilience: '🛡️', healing: '🩹',
      anxiety: '😰', sadness: '😢', anger: '😠', fatigue: '😴', confusion: '😕',
      overwhelm: '😵', isolation: '🏝️', shame: '😔', hopelessness: '😞', inertia: '🛑',
      reflection: '🤔', uncertainty: '❓', questioning: '🤷', rest: '😌', sleep: '😴',
      focus: '🎯', scatter: '💫', connection: '🤝', lonely: '🥺'
    };
    return emojiMap[tag] || '📝';
  };

  const handleSaveAsAnchor = async (entry: TimelineEntry) => {
    if (!user || !entry.symbolic_tags || entry.symbolic_tags.length === 0) return;
    
    try {
      setSavingAnchors(prev => ({ ...prev, [entry.id]: true }));
      
      // Create a title from the entry summary or tags
      const title = entry.summary 
        ? entry.summary.substring(0, 50) + (entry.summary.length > 50 ? '...' : '')
        : `Timeline Entry - ${entry.symbolic_tags.slice(0, 3).join(', ')}`;
      
      await apiService.createMemoryAnchor({
        title,
        date: entry.timestamp,
        tagsPresent: entry.symbolic_tags,
        reflectionNote: entry.summary ? `Timeline Entry: ${entry.summary}` : `Tags: ${entry.symbolic_tags.join(', ')}`,
        sourceType: 'tag_cluster',
        sourceId: entry.id,
        radarScores: {} // Timeline entries don't have radar scores, but we can add them later if needed
      });
      
      // Show success feedback
      alert('Memory anchor created successfully!');
    } catch (error) {
      console.error('Error saving anchor:', error);
      alert('Failed to create memory anchor. Please try again.');
    } finally {
      setSavingAnchors(prev => ({ ...prev, [entry.id]: false }));
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white  p-6 rounded-lg shadow-md">
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (!timelineData || timelineData.length === 0) {
    return (
      <div className="bg-white  p-6 rounded-lg shadow-md">
        <div className="text-center py-8">
          <div className="text-4xl mb-4">📝</div>
          <h3 className="text-lg font-medium mb-2">No Timeline Entries Yet</h3>
          <p className="text-gray-500 ">
            Start chatting to build your emotional timeline. Important moments will be automatically saved here.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white  p-6 rounded-lg shadow-md">
      <div className="space-y-6">
        {timelineData.slice(0, 10).map((entry: TimelineEntry, index: number) => (
          <div key={entry.id} className="flex items-start space-x-4 group">
            {/* Timeline dot */}
            <div className="flex-shrink-0">
              <div className="w-3 h-3 bg-blue-500 rounded-full mt-2"></div>
              {index < timelineData.length - 1 && (
                <div className="w-0.5 h-12 bg-gray-200  mx-auto mt-2"></div>
              )}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-500 ">
                  {new Date(entry.timestamp).toLocaleDateString('en-US', {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
                
                {/* Save as Anchor Button */}
                {entry.symbolic_tags && entry.symbolic_tags.length > 0 && (
                  <button
                    onClick={() => handleSaveAsAnchor(entry)}
                    disabled={savingAnchors[entry.id]}
                    className="p-1 text-gray-400 hover:text-indigo-600  opacity-0 group-hover:opacity-100 transition-all duration-200 disabled:opacity-50"
                    title="Save as memory anchor"
                  >
                    {savingAnchors[entry.id] ? (
                      <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <BookmarkIcon className="w-4 h-4" />
                    )}
                  </button>
                )}
              </div>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-3">
                {(entry.symbolic_tags || []).slice(0, 5).map((tag) => (
                  <span
                    key={tag}
                    className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getTagColor(tag)}`}
                  >
                    <span className="mr-1">{getTagEmoji(tag)}</span>
                    {tag}
                  </span>
                ))}
                {(entry.symbolic_tags || []).length > 5 && (
                  <span className="text-xs text-gray-500 ">
                    +{(entry.symbolic_tags || []).length - 5} more
                  </span>
                )}
              </div>

              {/* Content preview */}
              {entry.summary && (
                <p className="text-sm text-gray-700  line-clamp-2">
                  {entry.summary}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>

      {timelineData.length > 10 && (
        <div className="mt-6 text-center">
          <button className="text-blue-500 hover:text-blue-600 text-sm font-medium">
            View All Entries ({timelineData.length})
          </button>
        </div>
      )}
    </div>
  );
};

export default SymbolicTimeline; 